// ── EmployeeSummaryPage ("By Employee") — funnel counts & conversion rates per owner ──
import { useState, useEffect, useMemo } from "react";
import { B } from "../../../shared/theme";
import { fmt, exportCSV } from "../../../shared/format";
import { Btn, Card, ErrorMsg, ExportBtn, STh, SectionHeader, Spinner, Td, TdNum, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { parseDate, isExcludedOwner, OwnerMultiSelect } from "./analyticsShared";

const commit = (x) => parseFloat(x.commitment_amount || 0);
const initf = (x) => parseFloat(x.initial_funding_amount || 0);

export const EmployeeSummaryPage = () => {
  const { api, setModal } = useApp();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [created, setCreated] = useState({ from: "", to: "" });
  const [company, setCompany] = useState("");
  const [sourceSel, setSourceSel] = useState("");
  const [ownerSel, setOwnerSel] = useState(null);        // Set multi-select; null = default (all except excluded four)
  const [stageSel, setStageSel] = useState("");
  const [matrixSort, setMatrixSort] = useState("key");   // conversion-matrix column sort
  const [matrixDir, setMatrixDir] = useState("asc");
  const [empSel, setEmpSel] = useState("");              // click an employee row → filter detail

  const load = () => {
    setLoading(true);
    api.get("getOpportunities")
      .then(o => { setOpps(Array.isArray(o) ? o : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);

  const owners = useMemo(() => [...new Set(opps.map(o => o.ownerName).filter(Boolean))].sort(), [opps]);
  const sources = useMemo(() => [...new Set(opps.map(o => o.lead_source).filter(Boolean))].sort(), [opps]);
  const stages = useMemo(() => [...new Set(opps.map(o => o.stage).filter(Boolean))].sort(), [opps]);

  // default Sourced By to every owner except the excluded four
  useEffect(() => { if (ownerSel === null && owners.length) setOwnerSel(new Set(owners.filter(o => !isExcludedOwner(o)))); }, [owners, ownerSel]);
  const ownerOk = (name) => !name ? true : (ownerSel ? ownerSel.has(name) : !isExcludedOwner(name));

  const rows = useMemo(() => {
    const c = company.trim().toLowerCase();
    const fromD = created.from ? new Date(created.from) : null;
    const toD = created.to ? new Date(created.to) : null;
    return opps.filter(o => {
      if (!ownerOk(o.ownerName)) return false;
      if (c && !((o.companyname || o.name || "").toLowerCase().includes(c))) return false;
      if (sourceSel && o.lead_source !== sourceSel) return false;
      if (stageSel && o.stage !== stageSel) return false;
      if (fromD || toD) {
        const d = parseDate(o.createdat);
        if (!d) return false;
        if (fromD && d < fromD) return false;
        if (toD && d > toD) return false;
      }
      return true;
    });
  }, [opps, company, sourceSel, ownerSel, stageSel, created]);

  const { sorted: detailSorted, sortKey, sortDir, toggle } = useSortable(rows, 'companyname', 'asc');

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const isIssued = (o) => !!o.term_sheet_issued_date;
  const isSigned = (o) => !!o.term_sheet_signed_date;
  const isFunded = (o) => !!o.is_won;

  // group by owner
  const m = new Map();
  rows.forEach(o => {
    const k = o.ownerName || "—";
    const e = m.get(k) || { key: k, opp: 0, iss: 0, sig: 0, fun: 0 };
    e.opp += 1;
    if (isIssued(o)) e.iss += 1;
    if (isSigned(o)) e.sig += 1;
    if (isFunded(o)) e.fun += 1;
    m.set(k, e);
  });
  const mval = (r) => {
    switch (matrixSort) {
      case "opp": return r.opp;
      case "iss": return r.iss;
      case "sig": return r.sig;
      case "fun": return r.fun;
      case "issPct": return r.opp ? r.iss / r.opp : -1;
      case "sigPct": return r.iss ? r.sig / r.iss : -1;
      case "funPct": return r.sig ? r.fun / r.sig : -1;
      default: return r.key;
    }
  };
  const empRows = [...m.values()].sort((a, b) => {
    const av = mval(a), bv = mval(b);
    let cmp = matrixSort === "key" ? String(av).localeCompare(String(bv)) : ((av - bv) || a.key.localeCompare(b.key));
    return matrixDir === "asc" ? cmp : -cmp;
  });
  const clickMatrix = (key) => {
    if (matrixSort === key) setMatrixDir(d => d === "asc" ? "desc" : "asc");
    else { setMatrixSort(key); setMatrixDir(key === "key" ? "asc" : "desc"); }
  };
  const caret = (key) => matrixSort === key ? (matrixDir === "asc" ? " ▲" : " ▼") : "";
  const tot = empRows.reduce((s, r) => ({ opp: s.opp + r.opp, iss: s.iss + r.iss, sig: s.sig + r.sig, fun: s.fun + r.fun }), { opp: 0, iss: 0, sig: 0, fun: 0 });

  // detail filtered to the clicked employee (if any)
  const detailRows = empSel ? detailSorted.filter(o => (o.ownerName || "—") === empSel) : detailSorted;

  const pc = (n, d) => d > 0 ? (Math.round((n / d) * 1000) / 10) + "%" : "";

  const cardTitle = { fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" };
  const selStyle = { padding: "6px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, background: "var(--color-background-primary)", color: "var(--color-text-primary)" };
  const mth = { padding: "8px 10px", fontSize: 11, fontWeight: 600, color: "#fff", background: B.cyan, whiteSpace: "nowrap", textAlign: "right" };
  const mthL = { ...mth, textAlign: "left" };
  const mtd = { padding: "7px 10px", fontSize: 12, borderBottom: "0.5px solid var(--color-border-tertiary)", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
  const mtdL = { ...mtd, textAlign: "left", fontWeight: 500 };
  const ftd = { ...mtd, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", background: "var(--color-background-secondary)" };

  return <>
    <SectionHeader title="By Employee" />

    {/* slicers — two stacked cards */}
    <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Company…" style={{ ...selStyle, minWidth: 150 }} />
        <select value={sourceSel} onChange={e => setSourceSel(e.target.value)} style={selStyle}><option value="">All Lead Sources</option>{sources.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <OwnerMultiSelect owners={owners} value={ownerSel} onChange={setOwnerSel} />
        <select value={stageSel} onChange={e => setStageSel(e.target.value)} style={selStyle}><option value="">All Stages</option>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select>
      </Card>
      <Card style={{ padding: 10, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)" }}>
          Opp Created
          <input type="date" value={created.from} onChange={e => setCreated({ ...created, from: e.target.value })} style={selStyle} />
          <span>–</span>
          <input type="date" value={created.to} onChange={e => setCreated({ ...created, to: e.target.value })} style={selStyle} />
        </label>
      </Card>
    </div>

    {/* counts / conversion rates */}
    <Card style={{ padding: 0, marginBottom: 16, overflowX: "auto" }}>
      <div style={cardTitle}>Counts / Conversion Rates</div>
      <table style={{ borderCollapse: "collapse", whiteSpace: "nowrap", minWidth: "100%" }}>
        <thead><tr>
          <th style={{ ...mthL, cursor: "pointer" }} onClick={() => clickMatrix("key")}>Sourced By{caret("key")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("opp")}>Opp Cnt{caret("opp")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("issPct")}>Opp → TS Issued{caret("issPct")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("iss")}>TS Issued{caret("iss")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("sigPct")}>TS Issued → TS Signed{caret("sigPct")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("sig")}>TS Signed{caret("sig")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("funPct")}>TS Signed → Funded{caret("funPct")}</th>
          <th style={{ ...mth, cursor: "pointer" }} onClick={() => clickMatrix("fun")}>Funded{caret("fun")}</th>
        </tr></thead>
        <tbody>{empRows.length === 0 ? <tr><td style={mtdL} colSpan={8}>No opportunities in range.</td></tr> : empRows.map(r => {
          const active = empSel === r.key;
          return (
          <tr key={r.key} title="Click to filter the detail below by this employee" style={{ cursor: "pointer", background: active ? B.cyanLight : "" }}
            onClick={() => setEmpSel(prev => prev === r.key ? "" : r.key)}
            onMouseEnter={e => { if (!active) e.currentTarget.style.background = "var(--color-background-secondary)"; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.background = ""; }}>
            <td style={{ ...mtdL, fontWeight: active ? 700 : 500 }}>{r.key}</td>
            <td style={mtd}>{r.opp}</td>
            <td style={mtd}>{pc(r.iss, r.opp)}</td>
            <td style={mtd}>{r.iss || ""}</td>
            <td style={mtd}>{pc(r.sig, r.iss)}</td>
            <td style={mtd}>{r.sig || ""}</td>
            <td style={mtd}>{pc(r.fun, r.sig)}</td>
            <td style={mtd}>{r.fun || ""}</td>
          </tr>
          );
        })}</tbody>
        <tfoot><tr>
          <td style={{ ...ftd, textAlign: "left" }}>Total</td>
          <td style={ftd}>{tot.opp}</td>
          <td style={ftd}>{pc(tot.iss, tot.opp)}</td>
          <td style={ftd}>{tot.iss}</td>
          <td style={ftd}>{pc(tot.sig, tot.iss)}</td>
          <td style={ftd}>{tot.sig}</td>
          <td style={ftd}>{pc(tot.fun, tot.sig)}</td>
          <td style={ftd}>{tot.fun}</td>
        </tr></tfoot>
      </table>
    </Card>

    {/* detail */}
    <Card style={{ overflowX: "auto" }}>
      <div style={{ ...cardTitle, display: "flex", alignItems: "center", gap: 10 }}>
        Opportunity Detail
        {empSel && (
          <button onClick={() => setEmpSel("")} title="Clear employee filter"
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 9px", fontSize: 11, fontWeight: 600, border: "1px solid " + B.cyan, borderRadius: 999, background: B.cyanLight, color: B.cyanDark, cursor: "pointer", textTransform: "none", letterSpacing: 0 }}>
            {empSel} <span style={{ fontWeight: 700 }}>✕</span>
          </button>
        )}
        <span style={{ marginLeft: "auto" }}><ExportBtn onClick={() => exportCSV(detailRows, ["ownerName", "companyname", "stage", "lead_source", "term_sheet_issued_date", "term_sheet_signed_date", "is_won", "initial_funding_amount", "commitment_amount", "termination_reason"], "employee_detail.csv")}>Export CSV</ExportBtn></span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap", fontSize: 12 }}>
        <thead><tr>
          <STh label="Sourced By"     sortKey="ownerName"                currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Company Name"   sortKey="companyname"              currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Stage"          sortKey="stage"                    currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Lead Source"    sortKey="lead_source"              currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="TS Issued"      sortKey="term_sheet_issued_date"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="TS Signed"      sortKey="term_sheet_signed_date"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Funded"         sortKey="is_won"                   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
          <STh label="Initial Funding" sortKey="initial_funding_amount"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Commitment"     sortKey="commitment_amount"        currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric />
          <STh label="Termination Reason" sortKey="termination_reason"   currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
        </tr></thead>
        <tbody>{detailRows.length === 0 ? <tr><Td colSpan={10} style={{ color: "var(--color-text-secondary)" }}>No opportunities.</Td></tr> : detailRows.map(o => (
          <tr key={o.id} title="Open this opportunity" style={{ cursor: "pointer" }}
            onClick={() => setModal({ type: "opp-view", rec: o, accountid: o.accountid, onDone: load })}
            onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td>{o.ownerName || "—"}</Td>
            <Td style={{ fontWeight: 500, color: B.cyanDark }}>{o.companyname || o.name || "—"}</Td>
            <Td>{o.stage || "—"}</Td>
            <Td>{o.lead_source || "—"}</Td>
            <TdNum>{isIssued(o) ? 1 : ""}</TdNum>
            <TdNum>{isSigned(o) ? 1 : ""}</TdNum>
            <TdNum>{isFunded(o) ? 1 : ""}</TdNum>
            <TdNum>{fmt(initf(o))}</TdNum>
            <TdNum>{fmt(commit(o))}</TdNum>
            <Td>{o.termination_reason || "—"}</Td>
          </tr>
        ))}</tbody>
        <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
          <td colSpan={4} style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total ({detailRows.length})</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{detailRows.filter(isIssued).length}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{detailRows.filter(isSigned).length}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{detailRows.filter(isFunded).length}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailRows.reduce((s, o) => s + initf(o), 0))}</td>
          <td style={{ textAlign: "right", padding: "10px 12px", fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)", fontVariantNumeric: "tabular-nums" }}>{fmt(detailRows.reduce((s, o) => s + commit(o), 0))}</td>
          <td style={{ borderTop: "1.5px solid var(--color-border-secondary)" }} />
        </tr></tfoot>
      </table>
    </Card>
  </>;
};
